import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, filter, map, Observable, of, switchMap, take, tap, throwError } from 'rxjs';
import {User} from "./users.types";


@Injectable({
    providedIn: 'root'
})
export class UsersService
{
    // Private
    private _user: BehaviorSubject<User | null> = new BehaviorSubject(null);
    private _users: BehaviorSubject<User[] | null> = new BehaviorSubject(null);

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient)
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for contact
     */
    get user$(): Observable<User>
    {
        return this._user.asObservable();
    }

    /**
     * Getter for users
     */
    get users$(): Observable<User[]>
    {
        return this._users.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get contacts
     */
    getUsers(): Observable<User[]>
    {
        return this._httpClient.get<User[]>('api/user').pipe(
            tap((contacts) => {
                contacts.sort((a, b) => {
                    // Use toUpperCase() to ignore character casing
                    const nameA = a.lastname.toUpperCase();
                    const nameB = b.lastname.toUpperCase();

                    let comparison = 0;
                    if (nameA > nameB) {
                        comparison = 1;
                    } else if (nameA < nameB) {
                        comparison = -1;
                    }
                    return comparison;
                });
                this._users.next(contacts);
            })
        );
    }

    searchUsers(query: string, sort: string, direction: string): Observable<User[]|any>
    {
        return this._httpClient.get<User[]>('api/user', {params:
                {'filter': JSON.stringify({'filter': query, 'sort': sort, 'direction': direction})}
        }).pipe(
            tap((invoice) => {
                this._users.next(invoice);
            })
        );
    }

    /**
     * Search contacts with given query
     *
     * @param query
     */
    searchContacts(query: string): Observable<User[]|any>
    {

        return this._httpClient.get<User[]>('api/user', {params:
                {'filter': JSON.stringify({'filter': query})}
        }).pipe(
            tap((contacts) => {
                contacts.sort((a, b) => {
                    // Use toUpperCase() to ignore character casing
                    const nameA = a.lastname.toUpperCase();
                    const nameB = b.lastname.toUpperCase();

                    let comparison = 0;
                    if (nameA > nameB) {
                        comparison = 1;
                    } else if (nameA < nameB) {
                        comparison = -1;
                    }
                    return comparison;
                });
                this._users.next(contacts);
            })
        );
    }

    /**
     * Get user by id
     */
    getUserById(id: string): Observable<User>
    {
        return this._httpClient.get<User>(`api/user/${id}`).pipe(
            tap((user) => {
                this._user.next(user);
            })
        );

        return this._users.pipe(
            take(1),
            map((contacts) => {

                // Find the contact
                const contact = contacts.find(item => item.id === id) || null;

                // Update the contact
                this._user.next(contact);

                // Return the contact
                return contact;
            }),
            switchMap((contact) => {

                if ( !contact )
                {
                    return throwError('Could not found contact with id of ' + id + '!');
                }

                return of(contact);
            })
        );
    }

    /**
     * Create contact
     */
    createUser(): Observable<User>
    {
        const newUser: User = {
            firstname: "",
            lastname: "",
            email: ""

        };

        return this.user$.pipe(
            take(1),
            map(users => {
                // Update the contacts with the new contact
                //this._users.next([newUser, ...users]);
                this._user.next(newUser);
                // Return the new user
                return newUser;
            })
        );
    }

    /**
     * Post user
     *
     * @param user
     */
    postUser(user: User): Observable<User>
    {
        return this.users$.pipe(
            take(1),
            switchMap(users => this._httpClient.post<User>(`api/user`, user).pipe(
                map((createdUser) => {

                    // Find the index of the updated contact
                    // const index = users.findIndex(item => item.id === id);

                    // Update the contact
                    users = [...users, createdUser];
                    users.sort((a, b) => {
                        // Use toUpperCase() to ignore character casing
                        const lastnameA = a.lastname.toUpperCase();
                        const lastnameB = b.lastname.toUpperCase();

                        let comparison = 0;
                        if (lastnameA > lastnameB) {
                            comparison = 1;
                        } else if (lastnameA < lastnameB) {
                            comparison = -1;
                        }
                        return comparison;
                    });
                    // Update the contacts
                    this._users.next(users);

                    // Return the updated contact
                    return createdUser;
                }),
                switchMap(createdUser => this.user$.pipe(
                    take(1),
                    tap(() => {

                        // Update the contact if it's selected
                        this._user.next(createdUser);

                        // Return the updated contact
                        return createdUser;
                    })
                ))
            ))
        );
    }

    /**
     * Update user
     *
     * @param id
     * @param user
     */
    updateUser(id: string, user: User): Observable<User>
    {

        return this.users$.pipe(
            take(1),
            switchMap(users => this._httpClient.put<User>(`api/user/${id}`, user).pipe(
                map((updatedUser) => {

                    // Find the index of the updated contact
                    const index = users.findIndex(item => item.id === id);

                    // Update the contact
                    users[index] = updatedUser;

                    // Update the contacts
                    this._users.next(users);

                    // Return the updated contact
                    return updatedUser;
                }),
                switchMap(updatedUser => this.user$.pipe(
                    take(1),
                    filter(item => item && item.id === id),
                    tap(() => {

                        // Update the contact if it's selected
                        this._user.next(updatedUser);

                        // Return the updated contact
                        return updatedUser;
                    })
                ))
            ))
        );
    }

    /**
     * Delete contact
     *
     * @param id
     */
    deleteContact(id: string): Observable<boolean> {
        return this.users$.pipe(
            take(1),
            switchMap(contacts => this._httpClient.delete<boolean>(`api/user/${id}`).pipe(
                map((updatedPlace) => {
                        // Find the index of the deleted contact
                        const index = contacts.findIndex(item => item.id === id);

                        // Delete the contact
                        contacts.splice(index, 1);

                        // Update the contacts
                        this._users.next(contacts);

                        return updatedPlace == null;
                    }
                )
            ))
        );
    }

    /**
     * Update the avatar of the given contact
     *
     * @param id
     * @param avatar
     */
    uploadAvatar(id: string, avatar: File): Observable<User>
    {
        const formData: FormData = new FormData();
        formData.append("id", id);
        formData.append("file", avatar);
        return this.users$.pipe(
            take(1),
            switchMap(contacts => this._httpClient.post<User>('api/user/upload-avatar', formData
            ).pipe(
                map((updatedContact) => {

                    // Find the index of the updated contact
                    const index = contacts.findIndex(item => item.id === id);

                    // Update the contact
                    contacts[index] = updatedContact;

                    // Update the contacts
                    this._users.next(contacts);

                    // Return the updated contact
                    return updatedContact;
                }),
                switchMap(updatedContact => this.user$.pipe(
                    take(1),
                    filter(item => item && item.id === id),
                    tap(() => {

                        // Update the contact if it's selected
                        this._user.next(updatedContact);

                        // Return the updated contact
                        return updatedContact;
                    })
                ))
            ))
        );
    }

    setNewPassword(email: string, password: string): Observable<any>
    {
        return this._httpClient.post('api/auth/reset-password', {email, password}).pipe(
            switchMap((response: any) => {
                return of(response);
            })
        );
    }

    setRole(userId: string,accessRole: string): Observable<any>
    {
        return this._httpClient.post('api/roles/user/assign', {userId, accessRole});
    }

    impersonateUser(userId: string): Observable<any>
    {
        return this._httpClient.get(`api/user/${userId}/impersonate`);
    }

    leaveImpersonateUser(): Observable<any>
    {
        return this._httpClient.get('api/user/leave-impersonate');
    }

    resendEmail(email: string): Observable<any>
    {
        return this._httpClient.post('api/user/email/resend', {email});
    }

}
